/**
 * MobileEditorToolbar — the mobile equivalent of the desktop selection
 * bubble menu. Instead of appearing on selection, it's a persistent floating
 * bar anchored to the base of the writing page (mirrors design-proposals/mock-app.html
 * `.e-toolbar`): Bold, Italic, AI ghost-text toggle, Quote, Clear-format,
 * and a "More" button that opens a bottom Drawer with every formatting option.
 *
 * Rendered by ChapterEditor with `md:hidden` — desktop keeps the BubbleMenu.
 */
import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  Link as LinkIcon,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Pilcrow,
  RemoveFormatting,
  Eraser,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Menu as MenuIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAppStore } from '../stores/app-store';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { Switch } from './ui/switch';
import { GiGhost } from './icons/game-icons';
import { useState } from 'react';

interface MobileEditorToolbarProps {
  editor: Editor;
  /** true when the writing page renders as warm paper (= light theme) */
  paperMode: boolean;
}

function ToolIcon({
  onClick,
  active = false,
  glow = false,
  paperMode,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  /** extra ring highlight for the AI ghost-text toggle when it's on */
  glow?: boolean;
  paperMode: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-lg transition-colors',
        active
          ? paperMode
            ? 'bg-[#B3702A]/10 text-[#B3702A]'
            : 'bg-primary/15 text-primary'
          : paperMode
            ? 'text-[#453A5E]/70 hover:bg-[#453A5E]/5 hover:text-[#453A5E]'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        active && glow && 'ring-2 ring-primary/25',
      )}
    >
      {children}
    </button>
  );
}

/** Wraps a section's icon chips; wraps to a new line instead of leaving
 *  awkward empty grid cells when a group has fewer items than a fixed
 *  column count. */
function MenuRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

function MenuIconBtn({
  onClick,
  active = false,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'grid h-11 w-11 place-items-center rounded-xl ring-1 transition-colors',
        active
          ? 'bg-primary/15 text-primary ring-primary/30'
          : 'bg-muted/40 text-muted-foreground ring-border/60 hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {children}
    </label>
  );
}

// Marca-texto: a few tasteful highlight colors + a remover. Shared by the quick
// bar's popover and the drawer's "Marca-texto" section.
const HIGHLIGHT_COLORS = [
  { name: 'Amarelo', value: '#fef08a' },
  { name: 'Verde', value: '#bbf7d0' },
  { name: 'Azul', value: '#bfdbfe' },
  { name: 'Rosa', value: '#fbcfe8' },
  { name: 'Laranja', value: '#fed7aa' },
];

function HighlightColors({ editor, onPick }: { editor: Editor; onPick?: () => void }) {
  return (
    <div className="flex items-center gap-2">
      {HIGHLIGHT_COLORS.map((c) => (
        <button
          key={c.value}
          type="button"
          title={c.name}
          aria-label={c.name}
          onClick={() => {
            editor.chain().focus().toggleHighlight({ color: c.value }).run();
            onPick?.();
          }}
          className={cn(
            'h-8 w-8 rounded-full ring-1 ring-black/15 transition-transform active:scale-90',
            editor.isActive('highlight', { color: c.value }) && 'ring-2 ring-primary',
          )}
          style={{ backgroundColor: c.value }}
        />
      ))}
      <button
        type="button"
        aria-label="Remover marca-texto"
        onClick={() => {
          editor.chain().focus().unsetHighlight().run();
          onPick?.();
        }}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted-foreground ring-1 ring-border transition-transform active:scale-90"
      >
        <Eraser size={15} />
      </button>
    </div>
  );
}

export function MobileEditorToolbar({ editor, paperMode }: MobileEditorToolbarProps) {
  const { t } = useTranslation();
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hlOpen, setHlOpen] = useState(false);

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const toggleAiAutocomplete = () => {
    updateSettings({ aiAutocomplete: !settings.aiAutocomplete });
  };

  return (
    <>
      <div className="md:hidden relative mx-3.5 mb-2.5 flex-shrink-0">
      {/* Marca-texto: popover de cores acima do bar */}
      {hlOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 flex justify-center">
          <div
            className={cn(
              'rounded-2xl px-3 py-2 shadow-lg',
              paperMode ? 'bg-[#FFFDF4] ring-1 ring-[#d8ccb0]' : 'bg-popover ring-1 ring-border',
            )}
          >
            <HighlightColors editor={editor} onPick={() => setHlOpen(false)} />
          </div>
        </div>
      )}
      <div
        className={cn(
          'flex items-center justify-around gap-0.5 rounded-2xl px-1.5 py-1.5 shadow-lg',
          paperMode
            ? 'bg-[#FFFDF4] shadow-[0_8px_24px_-8px_rgba(69,58,94,0.25)] ring-1 ring-[#d8ccb0]'
            : 'bg-popover ring-1 ring-border',
        )}
        role="toolbar"
        aria-label={t('toolbar.label', 'Formatação de texto')}
      >
        <ToolIcon
          paperMode={paperMode}
          active={editor.isActive('bold')}
          title={t('toolbar.bold', 'Negrito')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          active={editor.isActive('italic')}
          title={t('toolbar.italic', 'Itálico')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          active={editor.isActive('highlight') || hlOpen}
          title={t('toolbar.highlight', 'Marca-texto')}
          onClick={() => setHlOpen((v) => !v)}
        >
          <Highlighter size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          active={settings.aiAutocomplete}
          glow
          title={
            settings.aiAutocomplete
              ? t('mobileToolbar.aiAutocompleteOn', 'Sugestão com IA ativada')
              : t('mobileToolbar.aiAutocompleteOff', 'Ativar sugestão com IA')
          }
          onClick={toggleAiAutocomplete}
        >
          <GiGhost size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          active={editor.isActive('blockquote')}
          title={t('toolbar.blockquote', 'Citação')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          title={t('toolbar.clearFormatting', 'Limpar Formatação')}
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          <Pilcrow size={16} />
        </ToolIcon>
        <ToolIcon
          paperMode={paperMode}
          title={t('mobileToolbar.moreOptions', 'Mais opções de formatação')}
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon size={16} />
        </ToolIcon>
      </div>
      </div>

      <Drawer open={menuOpen} onOpenChange={setMenuOpen}>
        <DrawerContent className="flex max-h-[75dvh] flex-col p-0">
          {/* Header — mirrors the app's other drawers (icon chip + mono uppercase title) */}
          <div className="flex items-center gap-2 border-b border-border px-4 pb-3 pt-1">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
              <MenuIcon size={15} />
            </span>
            <DrawerTitle className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">
              {t('mobileToolbar.menuTitle', 'Formatação')}
            </DrawerTitle>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {/* Text marks */}
            <div className="space-y-1.5">
              <SectionLabel>{t('mobileToolbar.sectionText', 'Texto')}</SectionLabel>
              <MenuRow>
                <MenuIconBtn
                  title={t('toolbar.bold', 'Negrito')}
                  active={editor.isActive('bold')}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                >
                  <Bold size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.italic', 'Itálico')}
                  active={editor.isActive('italic')}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                  <Italic size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.underline', 'Sublinhado')}
                  active={editor.isActive('underline')}
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                  <UnderlineIcon size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.strikethrough', 'Tachado')}
                  active={editor.isActive('strike')}
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                  <Strikethrough size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.link', 'Adicionar Link')}
                  active={editor.isActive('link')}
                  onClick={setLink}
                >
                  <LinkIcon size={17} />
                </MenuIconBtn>
              </MenuRow>
            </div>

            {/* Marca-texto — cores (o que faltava: escolher a cor) */}
            <div className="space-y-1.5">
              <SectionLabel>{t('toolbar.highlight', 'Marca-texto')}</SectionLabel>
              <HighlightColors editor={editor} />
            </div>

            {/* Headings */}
            <div className="space-y-1.5">
              <SectionLabel>{t('mobileToolbar.sectionHeadings', 'Títulos')}</SectionLabel>
              <MenuRow>
                <MenuIconBtn
                  title={t('toolbar.heading2', 'Título 2')}
                  active={editor.isActive('heading', { level: 2 })}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                >
                  <Heading2 size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.heading3', 'Título 3')}
                  active={editor.isActive('heading', { level: 3 })}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                >
                  <Heading3 size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.paragraph', 'Parágrafo')}
                  active={editor.isActive('paragraph')}
                  onClick={() => editor.chain().focus().setParagraph().run()}
                >
                  <Pilcrow size={17} />
                </MenuIconBtn>
              </MenuRow>
            </div>

            {/* Blocks */}
            <div className="space-y-1.5">
              <SectionLabel>{t('mobileToolbar.sectionBlocks', 'Blocos')}</SectionLabel>
              <MenuRow>
                <MenuIconBtn
                  title={t('toolbar.blockquote', 'Citação')}
                  active={editor.isActive('blockquote')}
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                >
                  <Quote size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.bulletList', 'Lista com Marcadores')}
                  active={editor.isActive('bulletList')}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                  <List size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.orderedList', 'Lista Numerada')}
                  active={editor.isActive('orderedList')}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                  <ListOrdered size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.clearFormatting', 'Limpar Formatação')}
                  onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                >
                  <RemoveFormatting size={17} />
                </MenuIconBtn>
              </MenuRow>
            </div>

            {/* Alignment */}
            <div className="space-y-1.5">
              <SectionLabel>{t('mobileToolbar.sectionAlign', 'Alinhamento')}</SectionLabel>
              <MenuRow>
                <MenuIconBtn
                  title={t('toolbar.alignLeft', 'Alinhar à Esquerda')}
                  active={editor.isActive({ textAlign: 'left' })}
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                >
                  <AlignLeft size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.alignCenter', 'Centralizar')}
                  active={editor.isActive({ textAlign: 'center' })}
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                >
                  <AlignCenter size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.alignRight', 'Alinhar à Direita')}
                  active={editor.isActive({ textAlign: 'right' })}
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                >
                  <AlignRight size={17} />
                </MenuIconBtn>
                <MenuIconBtn
                  title={t('toolbar.justify', 'Justificar')}
                  active={editor.isActive({ textAlign: 'justify' })}
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                >
                  <AlignJustify size={17} />
                </MenuIconBtn>
              </MenuRow>
            </div>

            {/* AI ghost-text settings.
                `data-vaul-no-drag` stops vaul's swipe-to-dismiss gesture
                recognizer from stealing the pointer on this subtree — without
                it, the Switch below only toggled intermittently because vaul
                calls setPointerCapture() on every pointerdown inside the
                drawer content, racing with the switch's own pointer handling. */}
            <div
              className="space-y-3 rounded-xl border border-border bg-muted/30 p-3"
              data-vaul-no-drag=""
            >
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <GiGhost size={16} className="text-primary" />
                  {t('mobileToolbar.sectionAi', 'Sugestão com IA')}
                </span>
                <Switch
                  checked={settings.aiAutocomplete}
                  onCheckedChange={(checked) => updateSettings({ aiAutocomplete: checked })}
                  aria-label={
                    settings.aiAutocomplete
                      ? t('mobileToolbar.aiAutocompleteOn', 'Sugestão com IA ativada')
                      : t('mobileToolbar.aiAutocompleteOff', 'Ativar sugestão com IA')
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/60 p-1">
                {(['short', 'long'] as const).map((len) => (
                  <button
                    key={len}
                    type="button"
                    disabled={!settings.aiAutocomplete}
                    onClick={() => updateSettings({ aiAutocompleteLength: len })}
                    className={cn(
                      'rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-40',
                      settings.aiAutocompleteLength === len
                        ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {len === 'short'
                      ? t('mobileToolbar.aiLengthShort', 'Curta')
                      : t('mobileToolbar.aiLengthLong', 'Longa')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
